import { useState, useCallback, useEffect } from 'react';
import { API_ENDPOINTS } from '../constants/ApiEndpoints';
import { apiRequest } from '../Api';

/**
 * Hook for managing questionnaire lifecycle
 * Handles: fetch definition, create response, update answers, complete, score
 *
 * @param {number} questionnaireId - Questionnaire ID
 * @param {number} patientId - Patient ID (for creating responses)
 * @param {number} orgId - Organisation ID
 * @returns {Object} { definition, response, answers, score, status, isLoading, error, methods }
 */
export const useQuestionnaire = (questionnaireId, patientId, orgId) => {
  const [definition, setDefinition] = useState(null);
  const [response, setResponse] = useState(null);
  const [answers, setAnswers] = useState({});
  const [score, setScore] = useState(null);
  const [status, setStatus] = useState('idle'); // idle, loading, editing, scoring
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [actualQuestionnaireId, setActualQuestionnaireId] = useState(questionnaireId);

  /**
   * Fetch questionnaire definition by ID
   * Falls back to finding by instrument name if ID lookup fails
   */
  const fetchDefinition = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      let data = null;
      let idToUse = questionnaireId;

      try {
        // Try direct ID lookup first
        data = await apiRequest(API_ENDPOINTS.QUESTIONNAIRE.GET(questionnaireId), {
          method: 'GET'
        });
        console.log('✓ Fetched questionnaire by ID:', questionnaireId);
      } catch (idErr) {
        console.warn('✗ Failed to fetch by ID:', questionnaireId, '- trying by instrument name...');

        // Fallback: fetch all questionnaires and find by matching instrument
        // The numeric IDs (1-7) correspond to instruments in order: psqi, ipaq-sf, predimed-sa, etc.
        const instrumentNames = ['psqi', 'ipaq-sf', 'predimed-sa', 'audit-c', 'stop-bang', 'ucla', 'sa-contextual'];
        const instrumentName = instrumentNames[questionnaireId - 1]; // Convert 1-based index to instrument name

        if (!instrumentName) {
          throw new Error(`Invalid questionnaire ID: ${questionnaireId}`);
        }

        console.log(`Looking for instrument: ${instrumentName}`);

        const allQuestionnaires = await apiRequest('/api/questionnaires', {
          method: 'GET'
        });

        console.log('Available questionnaires:', allQuestionnaires);

        const match = allQuestionnaires.find(q => q.instrument === instrumentName);
        if (!match) {
          throw new Error(`Questionnaire for instrument '${instrumentName}' not found in database`);
        }

        console.log(`✓ Found questionnaire by instrument: ${instrumentName} (ID: ${match.questionnaireId})`);
        idToUse = match.questionnaireId;

        // Now fetch the full definition using the correct ID
        data = await apiRequest(API_ENDPOINTS.QUESTIONNAIRE.GET(idToUse), {
          method: 'GET'
        });
        setActualQuestionnaireId(idToUse);
      }

      if (data) {
        // Flatten the response: API returns { title, description, definition: { items } }
        // but component expects { title, description, items, ... }
        const flattenedDefinition = {
          ...data,
          items: data.definition?.items || []  // Move items to top level
        };

        console.log('Questionnaire definition loaded:', {
          instrument: data.instrument,
          items: flattenedDefinition.items?.length || 0
        });
        console.log('Setting flattened definition:', flattenedDefinition);
        setDefinition(flattenedDefinition);
      } else {
        console.error('No data returned from API');
      }
      setStatus('idle');
    } catch (err) {
      setError(err.message);
      console.error('Failed to fetch questionnaire:', err);
    } finally {
      setIsLoading(false);
    }
  }, [questionnaireId]);

  /**
   * Create a new questionnaire response
   */
  const createResponse = useCallback(async () => {
    if (!patientId || !orgId) {
      setError('Patient ID and Organisation ID required');
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await apiRequest(
        `${API_ENDPOINTS.QUESTIONNAIRE.CREATE_RESPONSE(questionnaireId)}?patientId=${patientId}&orgId=${orgId}`,
        { method: 'POST' }
      );

      setResponse(result);
      setAnswers({});
      setStatus('editing');
      return result;
    } catch (err) {
      setError(err.message);
      console.error('Failed to create response:', err);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [questionnaireId, patientId, orgId]);

  /**
   * Retrieve existing response
   */
  const getResponse = useCallback(async (responseId) => {
    console.log('getResponse called with responseId:', responseId);
    setIsLoading(true);
    setError(null);

    try {
      const data = await apiRequest(API_ENDPOINTS.QUESTIONNAIRE.GET_RESPONSE(responseId), {
        method: 'GET'
      });
      console.log('Response fetched:', data);
      setResponse(data);
      setAnswers(data.answers || {});
      setStatus(data.status === 'completed' || data.status === 'scored' ? 'completed' : 'editing');
      return data;
    } catch (err) {
      setError(err.message);
      console.error('Failed to fetch response:', err);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Update an answer (local state)
   */
  const updateAnswer = useCallback((linkId, value) => {
    setAnswers((prev) => ({
      ...prev,
      [linkId]: value,
    }));
  }, []);

  /**
   * Save answers to backend (called by auto-save hook)
   */
  const saveAnswers = useCallback(async (answersToSave) => {
    if (!response?.responseId) {
      setError('No active response');
      return null;
    }

    try {
      const result = await apiRequest(
        API_ENDPOINTS.QUESTIONNAIRE.UPDATE_RESPONSE(response.responseId),
        {
          method: 'PATCH',
          body: JSON.stringify(answersToSave)
        }
      );
      return result;
    } catch (err) {
      setError(err.message);
      console.error('Failed to save answers:', err);
      return null;
    }
  }, [response]);

  /**
   * Complete the questionnaire response
   */
  const completeResponse = useCallback(async () => {
    if (!response?.responseId) {
      setError('No active response');
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await apiRequest(
        API_ENDPOINTS.QUESTIONNAIRE.COMPLETE_RESPONSE(response.responseId),
        { method: 'POST' }
      );

      setResponse({ ...response, status: 'completed' });
      setStatus('completed');
      return result;
    } catch (err) {
      setError(err.message);
      console.error('Failed to complete response:', err);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [response]);

  /**
   * Score the completed questionnaire
   */
  const scoreResponse = useCallback(async () => {
    if (!response?.responseId || response.status !== 'completed') {
      setError('Response must be completed before scoring');
      return null;
    }

    setIsLoading(true);
    setError(null);
    setStatus('scoring');

    try {
      const result = await apiRequest(
        API_ENDPOINTS.QUESTIONNAIRE.SCORE(response.responseId),
        { method: 'POST' }
      );

      setScore(result);
      setStatus('scored');
      return result;
    } catch (err) {
      setError(err.message);
      console.error('Failed to score response:', err);
      setStatus('completed');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [response]);

  /**
   * Reset to initial state
   */
  const reset = useCallback(() => {
    setDefinition(null);
    setResponse(null);
    setAnswers({});
    setScore(null);
    setStatus('idle');
    setError(null);
  }, []);

  // Fetch definition on mount
  useEffect(() => {
    if (questionnaireId) {
      fetchDefinition();
    }
  }, [questionnaireId, fetchDefinition]);

  return {
    definition,
    response,
    answers,
    score,
    status,
    isLoading,
    error,
    methods: {
      createResponse,
      getResponse,
      updateAnswer,
      saveAnswers,
      completeResponse,
      scoreResponse,
      reset,
    },
  };
};

export default useQuestionnaire;
